import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChakraConfirmationDialog } from 'app/components/Dialogs';
import { Link } from 'types/api.types';
import { fetchSongEffect, leaveSongEffect } from 'app/store/SongList/effects';
import { addAlertAction } from 'app/store/Alerts/slice';
import { routes } from 'app/routing/routesConfig';
import { Song } from 'types/song';
import { CollaboratorStatus } from 'types/collaborator.types';
import { AppDispatch } from 'app/store';
import { usePlaylistFeatureFlag } from 'featureFlags';

type Context = 'songList' | 'collaboratorList' | 'folderSongList';

export interface LeaveSongDialogProps {
  context: Context;
  song?: Song;
  open: boolean;
  dispatch: AppDispatch;
  onClose: () => void;
  isLastCollaborator?: boolean;
  leaveLink?: Link;
}

const getDialogMessage = ({
  song,
  isLastCollaborator,
  playlistFeatureEnabled,
}: {
  song?: Song;
  isLastCollaborator?: boolean;
  playlistFeatureEnabled: boolean;
}) => {
  const playlistWarningMessage = playlistFeatureEnabled
    ? ' Note any of your playlist using this songs audio files will also be affected.'
    : '';
  if (!song) {
    return '';
  }
  if (isLastCollaborator) {
    return (
      <>
        Are you sure you want to leave the song<i> {song.title} </i>? As you are
        the sole collaborator the song will be deleted permanently.
        {playlistWarningMessage}
      </>
    );
  }

  return (
    <>
      Are you sure you want to leave the song
      <i> {song.title} </i>?{playlistWarningMessage}
    </>
  );
};

export const LeaveSongDialog: React.FC<LeaveSongDialogProps> = ({
  context,
  dispatch,
  song,
  onClose,
  open: openProp,
  isLastCollaborator: isLastCollaboratorProp,
  leaveLink,
}) => {
  const { playlistFeatureEnabled } = usePlaylistFeatureFlag();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isLastCollaborator, setIsLastCollaborator] = useState(
    isLastCollaboratorProp
  );

  const dispatchSuccessMessage = (song: Song) =>
    dispatch(
      addAlertAction({ message: `You have left the song ${song.title}` })
    );

  const handleCollaboratorLeave = async (song: Song) => {
    try {
      await dispatch(
        leaveSongEffect({ songGuid: song.guid, href: leaveLink })
      ).unwrap();
      dispatchSuccessMessage(song);
      navigate(routes.songs.path, {
        state: {
          excludeSongGuid: song.guid,
        },
      });
    } catch (e) {
      // Handle error
    }
  };

  const handleSongLeave = async (song: Song) => {
    try {
      await dispatch(
        leaveSongEffect({
          songGuid: song.guid,
          href: song._links?.leaveSong,
        })
      ).unwrap();
      dispatchSuccessMessage(song);
      onClose();
    } catch (e) {
      // Handle error
    }
  };

  const handleAction = async (song: Song) => {
    if (context === 'collaboratorList') {
      handleCollaboratorLeave(song);
    } else {
      handleSongLeave(song);
    }
  };

  const handleOpen = useCallback(async () => {
    if (context === 'songList' && song) {
      try {
        const fetchedSong = await dispatch(
          fetchSongEffect({ guid: song.guid })
        ).unwrap();
        const acceptedCollaborators = fetchedSong.collaborators?.filter(
          (collaborator) => collaborator.status === CollaboratorStatus.ACCEPTED
        );
        setIsLastCollaborator(acceptedCollaborators?.length === 1);
        setOpen(true);
      } catch (e) {
        // handle error
      }
    } else {
      setOpen(true);
    }
  }, [dispatch, context, song]);

  useEffect(() => {
    if (openProp) {
      handleOpen();
    } else {
      setOpen(false);
    }
  }, [openProp, handleOpen]);

  useEffect(() => {
    setIsLastCollaborator(isLastCollaboratorProp);
  }, [isLastCollaboratorProp]);

  return (
    <ChakraConfirmationDialog
      title="Leave Song"
      actionButtonText="Leave"
      handleAction={() => song && handleAction(song)}
      isOpen={open}
      onClose={onClose}
      message={getDialogMessage({
        song,
        isLastCollaborator,
        playlistFeatureEnabled,
      })}
    />
  );
};
